// bluaIQ — Demo Data
// Used when backend is not running. Gives a realistic preview of the app.
// Demo project: 1,800 sqft spec home in Frisco, TX

export const DEMO_PROJECT = {
  id: 'demo-frisco-01',
  name: '4521 Legacy Dr — Frisco Spec',
  address: '4521 Legacy Dr',
  city: 'Frisco',
  state: 'TX',
  type: 'SPEC',
  sqftTarget: 1800,
  status: 'ACTIVE',
  phase: 3,
  startDate: '2026-01-15',
  createdAt: '2026-01-10T00:00:00Z',
}

export const DEMO_PROJECTS = [
  DEMO_PROJECT,
  {
    id: 'demo-mckinney-02',
    name: '810 Elm St — McKinney Spec',
    address: '810 Elm St',
    city: 'McKinney',
    state: 'TX',
    type: 'SPEC',
    sqftTarget: 2200,
    status: 'ACTIVE',
    phase: 1,
    startDate: '2026-03-01',
    createdAt: '2026-02-20T00:00:00Z',
  },
]

export const DEMO_PROJECT_IDS = new Set(DEMO_PROJECTS.map(p => p.id))
export const isDemoProject = (id: string | undefined) => !!id && DEMO_PROJECT_IDS.has(id)

export const DEMO_DASHBOARD = {
  project: DEMO_PROJECT,
  kpis: {
    totalCost: 285000,
    committed: 142500,
    committedPct: 50,
    progress: 45,
    pendingBids: 3,
    expiringInsurance: 1,
    totalSpent: 0,
    // PR-F3A: demo project has no anomalous contract amounts.
    committedExcludedCount: 0,
    committedExcludedAmount: 0,
  },
  alerts: [
    {
      type: 'insurance',
      severity: 'warning' as const,
      message: 'Póliza GL de ABC Insurance vence en 12 días',
      link: '/projects/demo-frisco-01/insurance',
    },
    {
      type: 'bid',
      severity: 'info' as const,
      message: '3 propuestas de plomería esperando revisión',
      link: '/projects/demo-frisco-01/bids',
    },
  ],
}

export const DEMO_LIFECYCLE = [
  // Phase 0 — Pre-development
  { id: 'lt-01', phase: 0, name: 'Land acquisition', nameEs: 'Adquisición de terreno', status: 'COMPLETED' as const, sortOrder: 1, durationDays: 14, estimatedDate: '2026-01-15', actualDate: '2026-01-14' },
  { id: 'lt-02', phase: 0, name: 'Soil test', nameEs: 'Estudio de suelo', status: 'COMPLETED' as const, sortOrder: 2, durationDays: 5, estimatedDate: '2026-01-20', actualDate: '2026-01-22' },
  { id: 'lt-03', phase: 0, name: 'Survey', nameEs: 'Levantamiento topográfico', status: 'COMPLETED' as const, sortOrder: 3, durationDays: 3, estimatedDate: '2026-01-25', actualDate: '2026-01-24' },
  // Phase 1 — Design & Permits
  { id: 'lt-04', phase: 1, name: 'Architectural plans', nameEs: 'Planos arquitectónicos', status: 'COMPLETED' as const, sortOrder: 1, durationDays: 21, estimatedDate: '2026-02-01' },
  { id: 'lt-05', phase: 1, name: 'Structural engineering', nameEs: 'Ingeniería estructural', status: 'COMPLETED' as const, sortOrder: 2, durationDays: 10, estimatedDate: '2026-02-15' },
  { id: 'lt-06', phase: 1, name: 'Building permit', nameEs: 'Permiso de construcción', status: 'COMPLETED' as const, sortOrder: 3, durationDays: 15, estimatedDate: '2026-02-28' },
  // Phase 2 — Contracting
  { id: 'lt-07', phase: 2, name: 'Foundation bid selection', nameEs: 'Selección bid cimentación', status: 'COMPLETED' as const, sortOrder: 1, durationDays: 7 },
  { id: 'lt-08', phase: 2, name: 'Framing contract', nameEs: 'Contrato de framing', status: 'COMPLETED' as const, sortOrder: 2, durationDays: 5 },
  { id: 'lt-09', phase: 2, name: 'MEP contracts', nameEs: 'Contratos MEP', status: 'IN_PROGRESS' as const, sortOrder: 3, durationDays: 7 },
  { id: 'lt-10', phase: 2, name: 'Roofing contract', nameEs: 'Contrato de techo', status: 'PENDING' as const, sortOrder: 4, durationDays: 5 },
  // Phase 3 — Construction
  { id: 'lt-11', phase: 3, name: 'Foundation pour', nameEs: 'Vaciado de cimentación', status: 'COMPLETED' as const, sortOrder: 1, durationDays: 3 },
  { id: 'lt-12', phase: 3, name: 'Framing', nameEs: 'Estructura (framing)', status: 'COMPLETED' as const, sortOrder: 2, durationDays: 14 },
  { id: 'lt-13', phase: 3, name: 'Rough plumbing', nameEs: 'Plomería rough-in', status: 'IN_PROGRESS' as const, sortOrder: 3, durationDays: 5 },
  { id: 'lt-14', phase: 3, name: 'Electrical rough', nameEs: 'Eléctrico rough-in', status: 'IN_PROGRESS' as const, sortOrder: 4, durationDays: 5 },
  { id: 'lt-15', phase: 3, name: 'HVAC rough', nameEs: 'HVAC rough-in', status: 'PENDING' as const, sortOrder: 5, durationDays: 4 },
  { id: 'lt-16', phase: 3, name: 'Insulation', nameEs: 'Aislamiento', status: 'PENDING' as const, sortOrder: 6, durationDays: 2 },
  { id: 'lt-17', phase: 3, name: 'Drywall', nameEs: 'Drywall', status: 'PENDING' as const, sortOrder: 7, durationDays: 7 },
  { id: 'lt-18', phase: 3, name: 'Roofing', nameEs: 'Techo', status: 'BLOCKED' as const, sortOrder: 8, durationDays: 3 },
  // Phase 4 — Closing
  { id: 'lt-19', phase: 4, name: 'Final inspection', nameEs: 'Inspección final', status: 'PENDING' as const, sortOrder: 1, durationDays: 1 },
  { id: 'lt-20', phase: 4, name: 'Closing', nameEs: 'Cierre', status: 'PENDING' as const, sortOrder: 2, durationDays: 3 },
]

export const DEMO_CONTACTS = [
  { id: 'c1', name: 'Carlos Mendez', role: 'CONTRACTOR', phone: '(469) 555-1234', email: 'carlos@mendezframing.com', company: 'Mendez Framing LLC', trade: 'Framing' },
  { id: 'c2', name: 'Mike Sullivan', role: 'SUPPLIER', phone: '(972) 555-5678', email: 'msullivan@84lumber.com', company: '84 Lumber', trade: 'Materials' },
  { id: 'c3', name: 'Rosa Gutierrez', role: 'INSPECTOR', phone: '(214) 555-9012', email: 'rgutierrez@friscocity.gov', company: 'City of Frisco', trade: '' },
  { id: 'c4', name: 'David Chen', role: 'ARCHITECT', phone: '(469) 555-3456', email: 'david@chenarch.com', company: 'Chen Architecture', trade: '' },
  { id: 'c5', name: 'Juan Torres', role: 'CONTRACTOR', phone: '(972) 555-7890', email: 'jtorres@torresplumbing.com', company: 'Torres Plumbing', trade: 'Plumbing' },
  { id: 'c6', name: 'Sarah Johnson', role: 'REALTOR', phone: '(214) 555-2345', email: 'sarah@kw.com', company: 'Keller Williams', trade: '' },
]

export const DEMO_BUDGET = {
  id: 'b1',
  version: 3,
  totalCost: 285000,
  lineItems: [
    { id: 'bl1', category: 'LAND', label: 'Terreno', amount: 65000 },
    { id: 'bl2', category: 'FOUNDATION', label: 'Cimentación', amount: 18500 },
    { id: 'bl3', category: 'FRAMING', label: 'Framing + materiales', amount: 42000 },
    { id: 'bl4', category: 'ROOFING', label: 'Techo', amount: 12800 },
    { id: 'bl5', category: 'MEP', label: 'Plomería + Eléctrico + HVAC', amount: 38000 },
    { id: 'bl6', category: 'INSULATION', label: 'Aislamiento', amount: 5200 },
    { id: 'bl7', category: 'DRYWALL', label: 'Drywall + Pintura', amount: 16500 },
    { id: 'bl8', category: 'FINISHES', label: 'Acabados (pisos, baños, cocina)', amount: 45000 },
    { id: 'bl9', category: 'EXTERIOR', label: 'Exterior (siding, landscaping)', amount: 22000 },
    { id: 'bl10', category: 'PERMITS', label: 'Permisos y fees', amount: 8500 },
    { id: 'bl11', category: 'CONTINGENCY', label: 'Contingencia (5%)', amount: 11500 },
  ],
}

export const DEMO_SUPPLIERS = [
  { id: 's1', name: '84 Lumber', city: 'Garland', state: 'TX', phone: '(972) 555-1111', email: 'quotes@84lumber.com', rating: 4.5 },
  { id: 's2', name: 'HD Supply', city: 'Dallas', state: 'TX', phone: '(214) 555-2222', email: 'pro@hdsupply.com', rating: 4.2 },
  { id: 's3', name: 'Carter Lumber', city: 'Mesquite', state: 'TX', phone: '(972) 555-3333', email: 'sales@carterlumber.com', rating: 4.7 },
  { id: 's4', name: 'Home Depot Pro', city: 'Plano', state: 'TX', phone: '(469) 555-4444', email: 'pro@homedepot.com', rating: 4.0 },
  { id: 's5', name: 'Beacon Roofing', city: 'Irving', state: 'TX', phone: '(972) 555-5555', email: 'orders@becn.com', rating: 4.3 },
]

export const DEMO_BIDS = [
  { id: 'bid1', contractorName: 'Torres Plumbing', trade: 'plumbing', totalAmount: 14500, laborOnly: false, materialsIncluded: true, scopeIncluded: ['Rough-in', 'Finish', 'Water heater install'], scopeExcluded: ['Gas line', 'Sewer tap'], timelineDays: 12, status: 'RECEIVED' },
  { id: 'bid2', contractorName: 'DFW Plumbing Pro', trade: 'plumbing', totalAmount: 16200, laborOnly: true, materialsIncluded: false, scopeIncluded: ['Rough-in', 'Finish', 'Gas line', 'Water heater'], scopeExcluded: ['Sewer tap', 'Fixtures'], timelineDays: 10, status: 'RECEIVED' },
  { id: 'bid3', contractorName: 'Reliable Pipes LLC', trade: 'plumbing', totalAmount: 13800, laborOnly: false, materialsIncluded: true, scopeIncluded: ['Rough-in', 'Finish'], scopeExcluded: ['Gas line', 'Water heater', 'Sewer tap'], timelineDays: 15, status: 'RECEIVED' },
]

// Helper to check if we're in demo mode (no backend)
export function isDemoMode(): boolean {
  return !import.meta.env.VITE_API_URL && import.meta.env.DEV
}
